import { Button } from "@/components/ui/baseUi/button"
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/baseUi/input-group"
import { Text } from "@/components/ui/baseUi/text"
import { PromoCodeColumnToSort } from "@/modules/finances/utils/financeUtils"
import useDebounce from "@/modules/hooks/useDebounce"
import { useLanguage } from "@/modules/language/hooks/useLanguage"
import { cn } from "@/modules/lib/utils"
import { PromoCodeListItem } from "@/modules/promoCode/components/promoCodeListItem"
import type { BoxPromoCodeEntity } from "@/modules/promoCode/entities/boxPromoCodeEntity"
import type { PromoCodeEntity } from "@/modules/promoCode/entities/promoCodeEntity"
import { getFormattedPromoCodeDuration, getFormattedPromoCodeReduction } from "@/modules/promoCode/utils/promoCodeUtils"
import { ColumnSortingOrder } from "@/modules/types/datatableTypes"
import { HUDatatable } from "@/modules/ui/components/dataTables/huDatatable"
import { HeaderTemplate } from "@/modules/ui/components/dataTables/templates/headerTemplate"
import { useUser } from "@/modules/users/hooks/useUser"
import PromocodeEmptyDataDarkIcon from "@images/empty/promocode_empty_dark.svg"
import PromocodeEmptyDataLightIcon from "@images/empty/promocode_empty_light.svg"
import { useCurrentBox } from "@modules/box/hooks/useBox"
import { BoxStaffPermissions } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@modules/boxStaff/hooks/useBoxStaffPermissions"
import { word } from "@modules/core/utils/i18n"
import { usePromoCodesBoxOrCoach } from "@modules/promoCode/hooks/usePromoCodes"
import { usePromoCodeSidebar } from "@modules/promoCode/hooks/usePromoCodeSidebar"
import { LoadingSpinner } from "@modules/ui/components/loading"
import { createColumnHelper } from "@tanstack/react-table"

import { Plus, SearchIcon, XIcon } from "lucide-react"
import { useState } from "react"

type PromoCodeListViewProps = {
  className?: string
} & React.ComponentProps<"div">

const columnHelper = createColumnHelper<PromoCodeEntity | BoxPromoCodeEntity>()

export function PromoCodeListView({ className, ...props }: PromoCodeListViewProps) {
  const { navigateToPromoCodeFormView, navigateToPromoCodeDetailsView } = usePromoCodeSidebar()
  const { result: box } = useCurrentBox()
  const user = useUser()
  const language = useLanguage()
  const hasEditFinancialPermission = useBoxStaffPermission(BoxStaffPermissions.EDIT_FINANCIAL)
  const canEditFinancial = !box || hasEditFinancialPermission
  const [nameFilter, setNameFilter] = useState<string | undefined>(undefined)
  const searchTermDebounced = useDebounce(nameFilter ?? undefined, 100)
  const [sorting, setSorting] = useState<
    | {
        column: (typeof PromoCodeColumnToSort)[keyof typeof PromoCodeColumnToSort]
        order: ColumnSortingOrder
      }
    | undefined
  >(undefined)

  // 0: valid codes, 1: expired codes
  const { promoCodes: activePromoCodes, isLoading: isActivePromoCodeLoading } = usePromoCodesBoxOrCoach(false)
  const { promoCodes: expiredPromoCodes, isLoading: isExpiredPromoCodeLoading } = usePromoCodesBoxOrCoach(true)

  const promoCodes = [...(activePromoCodes ?? []), ...(expiredPromoCodes ?? [])]
  const isLoading = isActivePromoCodeLoading || isExpiredPromoCodeLoading

  const filteredPromoCodes = searchTermDebounced
    ? promoCodes.filter(
        (promoCode) =>
          promoCode.code.toLowerCase().includes(searchTermDebounced.toLowerCase()) ||
          promoCode.title.toLowerCase().includes(searchTermDebounced.toLowerCase()),
      )
    : promoCodes

  const sortedPromoCodes = sorting
    ? filteredPromoCodes.sort((a, b) => {
        const column = sorting.column
        const order = sorting.order === ColumnSortingOrder.ASC ? 1 : -1

        if (column === PromoCodeColumnToSort.TITLE) {
          return a.title.toLowerCase().localeCompare(b.title.toLowerCase()) * order
        }
        if (column === PromoCodeColumnToSort.CODE) {
          return a.code.toLowerCase().localeCompare(b.code.toLowerCase()) * order
        }
        if (column === PromoCodeColumnToSort.TYPE) {
          return (a.value - b.value) * order
        }
        if (column === PromoCodeColumnToSort.DURATION_IN_MONTHS) {
          return ((a.durationInMonths ?? Infinity) - (b.durationInMonths ?? Infinity)) * order
        }
        if (column === PromoCodeColumnToSort.MAX_ATTENDEES) {
          return ((a.maxAttendees ?? Infinity) - (b.maxAttendees ?? Infinity)) * order
        }
        return 0
      })
    : filteredPromoCodes
  const totalElements = sortedPromoCodes.length

  const columns = [
    columnHelper.accessor("title", {
      header: () => (
        <HeaderTemplate
          title={word("promoCode.view.name_status")}
          variant={
            sorting?.column === PromoCodeColumnToSort.TITLE
              ? sorting?.order === ColumnSortingOrder.ASC
                ? "stringSortAsc"
                : "stringSortDesc"
              : "default"
          }
          onSortAsc={() => setSorting({ column: PromoCodeColumnToSort.TITLE, order: ColumnSortingOrder.ASC })}
          onSortDesc={() => setSorting({ column: PromoCodeColumnToSort.TITLE, order: ColumnSortingOrder.DESC })}
        />
      ),
      cell: ({ row: { original: data } }) => <PromoCodeListItem key={data.id} promoCode={data} />,
    }),
    columnHelper.accessor("code", {
      header: () => (
        <HeaderTemplate
          title={word("promoCode.view.code")}
          variant={
            sorting?.column === PromoCodeColumnToSort.CODE
              ? sorting?.order === ColumnSortingOrder.ASC
                ? "stringSortAsc"
                : "stringSortDesc"
              : "default"
          }
          onSortAsc={() => setSorting({ column: PromoCodeColumnToSort.CODE, order: ColumnSortingOrder.ASC })}
          onSortDesc={() => setSorting({ column: PromoCodeColumnToSort.CODE, order: ColumnSortingOrder.DESC })}
        />
      ),
      cell: ({ row: { original: data } }) => (
        <Text fontStyle="BM" className="text-textDefault-body">
          {data.code}
        </Text>
      ),
    }),
    columnHelper.accessor("type", {
      header: () => (
        <HeaderTemplate
          title={word("promoCode.view.type_discount")}
          variant={
            sorting?.column === PromoCodeColumnToSort.TYPE
              ? sorting?.order === ColumnSortingOrder.ASC
                ? "numberSortAsc"
                : "numberSortDesc"
              : "default"
          }
          onSortAsc={() => setSorting({ column: PromoCodeColumnToSort.TYPE, order: ColumnSortingOrder.ASC })}
          onSortDesc={() => setSorting({ column: PromoCodeColumnToSort.TYPE, order: ColumnSortingOrder.DESC })}
        />
      ),
      cell: ({ row: { original: data } }) => (
        <Text fontStyle="BM" className="text-textDefault-body">
          {getFormattedPromoCodeReduction(
            data.type,
            data.value,
            language,
            box?.stripeAccountCurrency ?? user.stripeAccountCurrency ?? undefined,
          )}
        </Text>
      ),
    }),
    columnHelper.accessor("durationInMonths", {
      header: () => (
        <HeaderTemplate
          title={word("promoCode.view.duration")}
          variant={
            sorting?.column === PromoCodeColumnToSort.DURATION_IN_MONTHS
              ? sorting?.order === ColumnSortingOrder.ASC
                ? "numberSortAsc"
                : "numberSortDesc"
              : "default"
          }
          onSortAsc={() =>
            setSorting({ column: PromoCodeColumnToSort.DURATION_IN_MONTHS, order: ColumnSortingOrder.ASC })
          }
          onSortDesc={() =>
            setSorting({ column: PromoCodeColumnToSort.DURATION_IN_MONTHS, order: ColumnSortingOrder.DESC })
          }
        />
      ),
      cell: ({ row: { original: data } }) => (
        <Text fontStyle="BM" className="text-textDefault-body">
          {getFormattedPromoCodeDuration(data.durationInMonths)}
        </Text>
      ),
    }),
    columnHelper.accessor("maxAttendees", {
      header: () => (
        <HeaderTemplate
          title={word("promoCode.view.customers_number")}
          variant={
            sorting?.column === PromoCodeColumnToSort.MAX_ATTENDEES
              ? sorting?.order === ColumnSortingOrder.ASC
                ? "numberSortAsc"
                : "numberSortDesc"
              : "default"
          }
          onSortAsc={() => setSorting({ column: PromoCodeColumnToSort.MAX_ATTENDEES, order: ColumnSortingOrder.ASC })}
          onSortDesc={() => setSorting({ column: PromoCodeColumnToSort.MAX_ATTENDEES, order: ColumnSortingOrder.DESC })}
        />
      ),
      cell: ({ row: { original: data } }) => (
        <Text fontStyle="BM" className="text-textDefault-body">
          {data.maxAttendees ?? word("promoCode.view.unlimited")}
        </Text>
      ),
    }),
  ]

  const handleCreatePromoCode = () => {
    if (!canEditFinancial) return
    navigateToPromoCodeFormView()
  }

  return (
    <div className={cn("flex w-full flex-1 flex-col gap-2 px-5", className)} {...props}>
      <div className="flex flex-row gap-4">
        {promoCodes.length > 0 && (
          <InputGroup className="max-w-64 min-w-60">
            <InputGroupInput
              placeholder={word("global.search.placeholder")}
              onChange={(e) => setNameFilter(e.currentTarget.value)}
              value={nameFilter ?? ""}
            />
            <InputGroupAddon align="inline-start">
              <SearchIcon color="currentColor" />
            </InputGroupAddon>
            {nameFilter && (
              <InputGroupAddon align="inline-end" onClick={() => setNameFilter("")}>
                <XIcon color="currentColor" className="cursor-pointer" />
              </InputGroupAddon>
            )}
          </InputGroup>
        )}
        <Button variant="primary" size="lg" onClick={handleCreatePromoCode} disabled={!canEditFinancial}>
          <Plus />
          {word("promoCode.list.createButton")}
        </Button>
      </div>

      {isLoading ? (
        <div className="mt-4">
          <LoadingSpinner />
        </div>
      ) : sortedPromoCodes.length === 0 ? (
        <EmptyView
          title={
            promoCodes.length === 0
              ? word("promoCode.list.valid.empty.title")
              : word("promoCode.list.search.empty.title")
          }
          description={promoCodes.length === 0 ? word("promoCode.list.valid.empty.description") : undefined}
          onButtonClick={handleCreatePromoCode}
        />
      ) : (
        <HUDatatable
          data={sortedPromoCodes}
          columns={columns}
          handleRowSelectionChange={(row) => void navigateToPromoCodeDetailsView(row)}
          totalElements={totalElements}
          className="*:table-fixed"
        />
      )}
    </div>
  )
}

function EmptyView({
  title,
  description,
  onButtonClick,
}: {
  title: string
  description?: string
  onButtonClick: () => void
}) {
  const box = useCurrentBox()

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 rounded-4xl bg-surface-background-secondary px-4">
      <img
        src={box ? PromocodeEmptyDataDarkIcon : PromocodeEmptyDataLightIcon}
        alt="Promocode Empty Data"
        className="max-w-[60%]"
      />
      <Text fontStyle="HS" className="text-textDefault-heading">
        {title}
      </Text>
      {description && (
        <Text fontStyle="BM" className="text-center wrap-break-word whitespace-break-spaces text-textDefault-heading">
          {description}
        </Text>
      )}
      <Button variant="primary" size="lg" onClick={onButtonClick}>
        <Plus />
        {word("promoCode.list.createButton")}
      </Button>
    </div>
  )
}
